import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Typography, Box, CircularProgress, Alert, Button, TextField, MenuItem, Select, InputLabel, FormControl, Checkbox, Autocomplete, IconButton, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";

import { useCurrentUser } from '../hooks/useCurrentUser';

import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DownloadIcon from '@mui/icons-material/Download';
import { getAbsencesForUsers } from '../utils/absenceService';
import {
  buildAbsencesByUserIdMap,
  fetchMonthlyWorklogSummaryForUsers,
  sortUsersByDisplayName,
  accumulateMonthlySummaryCell,
  pivotMinutesForDay,
} from '../utils/peopleManagerApi';
import {
  downloadAbsenceCalendarWorkbook,
  fetchDirectReportsByPmId,
  fetchManagedUsersHierarchy,
  mapHierarchyUserForSearch,
  sortPeopleManagerUsersForCalendar,
} from '../utils/absenceCalendarExport';
import { isFutureDay, parseLocalDate } from '../utils/dateHelpers';
import { FORECAST_CONFIG } from '../config/local.config';

export default function MonthlyPeopleManagerView() {
  const { userId, userName } = useCurrentUser();
  const navigate = useNavigate();

  const [managedUsers, setManagedUsers] = useState([]); // Solo report diretti (vista a cascata)
  const [hierarchyUsers, setHierarchyUsers] = useState([]); // Gerarchia completa, solo per ricerca
  const [hierarchyUsersLoading, setHierarchyUsersLoading] = useState(false);
  const [hierarchyLoadError, setHierarchyLoadError] = useState(null);
  const [managedUsersInitialized, setManagedUsersInitialized] = useState(false);
  const [activities, setActivities] = useState([]);

  // Refs per evitare chiamate duplicate
  const isFetchingUsers = useRef(false);
  const isFetchingHierarchy = useRef(false);
  const hierarchyFetched = useRef(false);
  const hierarchyDataRef = useRef([]);
  const hierarchyLoadPromiseRef = useRef(null);
  const isFetchingActivities = useRef(false);
  const isFetchingPivotData = useRef(false);
  const lastPivotFetchKey = useRef('');
  const [filtersReady, setFiltersReady] = useState(false);
  const hasAutoSetFilterUsers = useRef(false);

  // Filtri applicati (alimentano tabella e assenze)
  const [filterUsers, setFilterUsers] = useState([]);
  /** direct = solo report diretti; all = tutta la gerarchia; indirect = solo indiretti; custom = selezione esplicita */
  const [userFilterMode, setUserFilterMode] = useState('direct');
  const [filterActivityIds, setFilterActivityIds] = useState([]);
  // Filtri pending (modificati in UI, applicati con Apply)
  const [pendingFilterUsers, setPendingFilterUsers] = useState([]);
  const [pendingUserFilterMode, setPendingUserFilterMode] = useState('direct');
  const [pendingFilterActivityIds, setPendingFilterActivityIds] = useState([]);
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [pendingFilterMonth, setPendingFilterMonth] = useState(now.getMonth() + 1);
  const [pendingFilterYear, setPendingFilterYear] = useState(now.getFullYear());
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  const getSessionStorageKey = () => (userId ? `monthlyPmViewFilters:${userId}` : null);

  const loadSessionFilters = () => {
    const key = getSessionStorageKey();
    if (!key) return null;
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saveSessionFilters = (filters) => {
    const key = getSessionStorageKey();
    if (!key) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(filters));
    } catch {
      // ignore quota errors
    }
  };

  const applyFilters = () => {
    setUserFilterMode(pendingUserFilterMode);
    setFilterUsers(pendingFilterUsers);
    setFilterMonth(pendingFilterMonth);
    setFilterYear(pendingFilterYear);
    setFilterActivityIds(pendingFilterActivityIds);
    saveSessionFilters({
      userFilterMode: pendingUserFilterMode,
      filterUsers: pendingFilterUsers,
      filterMonth: pendingFilterMonth,
      filterYear: pendingFilterYear,
      filterActivityIds: pendingFilterActivityIds,
    });
  };

  const resetFilters = () => {
    const defaultMonth = now.getMonth() + 1;
    const defaultYear = now.getFullYear();
    setPendingFilterUsers([]);
    setPendingUserFilterMode('direct');
    setPendingFilterMonth(defaultMonth);
    setPendingFilterYear(defaultYear);
    setPendingFilterActivityIds([]);
    setFilterUsers([]);
    setUserFilterMode('direct');
    setFilterMonth(defaultMonth);
    setFilterYear(defaultYear);
    setFilterActivityIds([]);
    saveSessionFilters({
      userFilterMode: 'direct',
      filterUsers: [],
      filterMonth: defaultMonth,
      filterYear: defaultYear,
      filterActivityIds: [],
    });
  };

  const shiftMonth = (delta) => {
    const d = new Date(pendingFilterYear, pendingFilterMonth - 1 + delta, 1);
    setPendingFilterMonth(d.getMonth() + 1);
    setPendingFilterYear(d.getFullYear());
  };

  // Report diretti: alimentano tabella e navigazione a cascata
  useEffect(() => {
    if (!userId) {
      setManagedUsers([]);
      setManagedUsersLoaded(false);
      return;
    }

    if (isFetchingUsers.current) {
      return;
    }

    hasAutoSetFilterUsers.current = false;
    hierarchyFetched.current = false;
    hierarchyDataRef.current = [];
    hierarchyLoadPromiseRef.current = null;

    setManagedUsersInitialized(false);
    setManagedUsers([]);
    setHierarchyUsers([]);
    setHierarchyLoadError(null);
    setFilterUsers([]);
    setPendingFilterUsers([]);
    setUserFilterMode('direct');
    setPendingUserFilterMode('direct');
    setFilterActivityIds([]);
    setPendingFilterActivityIds([]);
    setManagedUsersLoaded(false);
    setFiltersReady(false);
    setPivotData({});
    lastPivotFetchKey.current = '';

    isFetchingUsers.current = true;

    fetchDirectReportsByPmId(userId)
      .then((formattedUsers) => {
        setManagedUsers(sortUsersByDisplayName(formattedUsers));
        setManagedUsersLoaded(true);
        setManagedUsersInitialized(true);
        isFetchingUsers.current = false;
      })
      .catch((err) => {
        console.error('Error fetching managed users:', err);
        setManagedUsers([]);
        setManagedUsersLoaded(true);
        setManagedUsersInitialized(true);
        isFetchingUsers.current = false;
      });
  }, [userId]);

  const ensureHierarchyLoaded = useCallback(async () => {
    if (!userId) return [];
    if (hierarchyFetched.current) {
      return hierarchyDataRef.current;
    }
    if (hierarchyLoadPromiseRef.current) {
      return hierarchyLoadPromiseRef.current;
    }

    isFetchingHierarchy.current = true;
    setHierarchyUsersLoading(true);
    setHierarchyLoadError(null);

    const promise = fetchManagedUsersHierarchy(userId, userName || 'Direct reports')
      .then((users) => {
        const formatted = users.map((user) => mapHierarchyUserForSearch(user, userId));
        hierarchyDataRef.current = sortUsersByDisplayName(formatted);
        hierarchyFetched.current = true;
        setHierarchyUsers(hierarchyDataRef.current);
        return hierarchyDataRef.current;
      })
      .catch((err) => {
        console.error('Error fetching hierarchy:', err);
        hierarchyFetched.current = false;
        hierarchyDataRef.current = [];
        setHierarchyUsers([]);
        setHierarchyLoadError('Unable to load the full team hierarchy. Direct reports are still available.');
        throw err;
      })
      .finally(() => {
        isFetchingHierarchy.current = false;
        setHierarchyUsersLoading(false);
        hierarchyLoadPromiseRef.current = null;
      });

    hierarchyLoadPromiseRef.current = promise;
    return promise;
  }, [userId, userName]);

  const selectHierarchyModePending = async (mode) => {
    if (mode === 'direct') {
      setPendingUserFilterMode('direct');
      setPendingFilterUsers([]);
      return;
    }
    try {
      setHierarchyLoadError(null);
      await ensureHierarchyLoaded();
      setPendingUserFilterMode(mode);
      setPendingFilterUsers([]);
    } catch {
      setHierarchyLoadError('Unable to load the full team hierarchy. Direct reports are still available.');
    }
  };

  useEffect(() => {
    if (!userId || managedUsers.length === 0 || !hasAutoSetFilterUsers.current) return;
    if (userFilterMode !== 'all' && userFilterMode !== 'indirect') return;
    if (hierarchyFetched.current) return;
    ensureHierarchyLoaded().catch(() => {});
  }, [userId, managedUsers.length, userFilterMode, ensureHierarchyLoaded]);

  // Al caricamento: sempre report diretti; dal session storage solo mese/anno/attività
  useEffect(() => {
    if (managedUsers.length > 0 && !hasAutoSetFilterUsers.current) {
      const session = loadSessionFilters();
      const month = session?.filterMonth || now.getMonth() + 1;
      const year = session?.filterYear || now.getFullYear();
      const activities = Array.isArray(session?.filterActivityIds) ? session.filterActivityIds : [];

      setPendingFilterUsers([]);
      setFilterUsers([]);
      setPendingUserFilterMode('direct');
      setUserFilterMode('direct');
      setPendingFilterMonth(month);
      setFilterMonth(month);
      setPendingFilterYear(year);
      setFilterYear(year);
      setPendingFilterActivityIds(activities);
      setFilterActivityIds(activities);

      hasAutoSetFilterUsers.current = true;
      setFiltersReady(true);
    }
  }, [managedUsers, userId]);

  // Utenti risolvibili nel filtro: diretti + annidati (dopo caricamento lazy)
  const usersForFilterLookup = useMemo(() => {
    const byId = new Map();
    managedUsers.forEach((u) => byId.set(u.id, u));
    hierarchyUsers.forEach((u) => {
      if (!byId.has(u.id)) byId.set(u.id, u);
    });
    return sortUsersByDisplayName(Array.from(byId.values()));
  }, [managedUsers, hierarchyUsers]);

  const searchAutocompleteOptions = useMemo(() => {
    const directIds = new Set(managedUsers.map((u) => u.id));
    const nestedOnly = hierarchyUsers.filter((u) => !directIds.has(u.id));
    return sortUsersByDisplayName([...managedUsers, ...nestedOnly]);
  }, [managedUsers, hierarchyUsers]);

  const directReportIds = useMemo(
    () => new Set(managedUsers.map((u) => u.id)),
    [managedUsers]
  );

  const indirectUsers = useMemo(
    () => hierarchyUsers.filter((u) => !directReportIds.has(u.id)),
    [hierarchyUsers, directReportIds]
  );

  const allHierarchyUserCount = useMemo(
    () => (hierarchyUsers.length > 0 ? usersForFilterLookup.length : managedUsers.length),
    [usersForFilterLookup.length, hierarchyUsers.length, managedUsers.length]
  );

  const filtersDirty = useMemo(() => (
    pendingFilterMonth !== filterMonth
    || pendingFilterYear !== filterYear
    || pendingUserFilterMode !== userFilterMode
    || JSON.stringify([...pendingFilterUsers].sort()) !== JSON.stringify([...filterUsers].sort())
    || JSON.stringify([...pendingFilterActivityIds].sort()) !== JSON.stringify([...filterActivityIds].sort())
  ), [
    pendingFilterMonth, filterMonth, pendingFilterYear, filterYear,
    pendingUserFilterMode, userFilterMode,
    pendingFilterUsers, filterUsers, pendingFilterActivityIds, filterActivityIds,
  ]);

  const appliedUserIdsForData = useMemo(() => {
    if (userFilterMode === 'all') {
      if (hierarchyUsers.length === 0) {
        return managedUsers.map((u) => u.id);
      }
      return usersForFilterLookup.map((u) => u.id);
    }
    if (userFilterMode === 'indirect') {
      return indirectUsers.map((u) => u.id);
    }
    if (userFilterMode === 'custom' && filterUsers.length > 0) {
      return usersForFilterLookup
        .filter((u) => filterUsers.includes(u.name || u.username))
        .map((u) => u.id);
    }
    return managedUsers.map((u) => u.id);
  }, [userFilterMode, filterUsers, managedUsers, usersForFilterLookup, indirectUsers, hierarchyUsers.length]);

  const resolveAppliedUserNames = useCallback(() => {
    if (userFilterMode === 'all') {
      return usersForFilterLookup.map((u) => u.name || u.username);
    }
    if (userFilterMode === 'indirect') {
      return indirectUsers.map((u) => u.name || u.username);
    }
    if (userFilterMode === 'custom' && filterUsers.length > 0) {
      return usersForFilterLookup
        .filter((u) => filterUsers.includes(u.name || u.username))
        .map((u) => u.name || u.username);
    }
    return managedUsers.map((u) => u.name || u.username);
  }, [userFilterMode, filterUsers, managedUsers, usersForFilterLookup, indirectUsers]);

  const activeFilterSummary = useMemo(() => {
    let userLabel;
    if (userFilterMode === 'all') {
      userLabel = `All reports (${allHierarchyUserCount})`;
    } else if (userFilterMode === 'indirect') {
      userLabel = `Indirect reports (${indirectUsers.length})`;
    } else if (userFilterMode === 'custom' && filterUsers.length > 0) {
      userLabel = `${filterUsers.length} user${filterUsers.length === 1 ? '' : 's'}`;
    } else {
      userLabel = `All direct reports (${managedUsers.length})`;
    }
    const activityLabel = filterActivityIds.length === 0
      ? 'All activities'
      : `${filterActivityIds.length} activit${filterActivityIds.length === 1 ? 'y' : 'ies'}`;
    return `${userLabel} · ${monthNames[filterMonth - 1]} ${filterYear} · ${activityLabel}`;
  }, [
    userFilterMode, filterUsers, managedUsers.length, indirectUsers.length,
    allHierarchyUserCount, filterMonth, filterYear, filterActivityIds, monthNames,
  ]);

  // Carica Activities per il filtro
  useEffect(() => {
    // Evita chiamate duplicate
    if (isFetchingActivities.current || activities.length > 0) {
      return;
    }

    isFetchingActivities.current = true;
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        const arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr : []);
        isFetchingActivities.current = false;
      })
      .catch(() => {
        setActivities([]);
        isFetchingActivities.current = false;
      });
  }, [activities.length]);

  // Rimosso fetchWorklogs standalone - questa vista usa solo la tabella pivot

  // Filtro rimosso - ora usiamo solo la tabella pivot filtrata

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  // const getEditableFields = () => {
  //   if (!columns.length) return [];
  //   return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  // };

  // Gestione apertura dialog - non utilizzata in questa vista (solo visualizzazione)

  // Rimuovo la funzione handleExportExcel

  // Azioni e colonne - non utilizzate in questa vista (solo visualizzazione)

  // Stato per la tabella pivot
  const [pivotData, setPivotData] = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  // const [pivotMode, setPivotMode] = useState('user'); // 'user' o 'project'
  // const [pivotDetails, setPivotDetails] = useState({});
  // Stato per l'utente selezionato per i dettagli (per utenti non People Manager)
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [detailsPivotData, setDetailsPivotData] = useState({});
  const [teamPivotData, setTeamPivotData] = useState({});
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);

  // Evita flash di utenti non autorizzati prima che siano caricati
  const [managedUsersLoaded, setManagedUsersLoaded] = useState(false);
  const [absencesByUserId, setAbsencesByUserId] = useState({});
  const [exportAbsencesLoading, setExportAbsencesLoading] = useState(false);

  const handleExportHierarchyAbsences = async () => {
    if (!userId) return;
    setExportAbsencesLoading(true);
    try {
      const hierarchyUsersForExport = await ensureHierarchyLoaded();
      if (!hierarchyUsersForExport.length) {
        window.alert('No users found in your hierarchy.');
        return;
      }
      const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
      const hierarchyIds = hierarchyUsersForExport.map((u) => u.id);
      const absencesEntries = await getAbsencesForUsers(hierarchyIds, yearMonth);
      const absencesForExport = buildAbsencesByUserIdMap(absencesEntries);
      const members = sortPeopleManagerUsersForCalendar(hierarchyUsersForExport);
      downloadAbsenceCalendarWorkbook({
        scopeLabel: 'People_Manager',
        year: filterYear,
        month: filterMonth,
        members,
        absencesByUserId: absencesForExport,
        secondColumnLabel: 'People Manager',
      });
    } catch (err) {
      console.error('Export absences failed:', err);
      window.alert(err?.message || 'Export absences failed. Please try again.');
    } finally {
      setExportAbsencesLoading(false);
    }
  };

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  // Dimensioni fisse per tabella responsive con scroll orizzontale
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140; // px
    const dayColWidth = 45; // px per ciascun giorno
    const totalColWidth = 60; // px
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  // Funzione per formattare i nomi utente (abbrevia solo la prima parte)
  const formatUserName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return fullName;

    const parts = fullName.trim().split(' ');
    if (parts.length <= 1) return fullName;

    // Abbrevia solo la prima parte: prima lettera + punto
    const firstPart = parts[0];
    const abbreviatedFirst = firstPart.charAt(0) + '.';

    // Mantieni il resto del nome
    const restOfName = parts.slice(1).join(' ');

    return `${abbreviatedFirst} ${restOfName}`;
  };

  // Calcola i nomi dei giorni della settimana per il mese selezionato
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend per il mese selezionato
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay(); // 0=Sunday, 6=Saturday
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    if (!managedUsersLoaded || appliedUserIdsForData.length === 0) {
      setAbsencesByUserId({});
      return;
    }
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    getAbsencesForUsers(appliedUserIdsForData, yearMonth)
      .then((entries) => setAbsencesByUserId(buildAbsencesByUserIdMap(entries)))
      .catch(() => setAbsencesByUserId({}));
  }, [filterYear, filterMonth, appliedUserIdsForData, managedUsersLoaded]);

  const userIdByUsername = useMemo(() => {
    const map = {};
    (usersForFilterLookup || []).forEach(u => {
      if (!u.id) return;
      const uid = String(u.id);
      if (u.name) map[u.name] = uid;
      if (u.username) map[u.username] = uid;
      if (u.first_name && u.last_name) map[`${u.first_name} ${u.last_name}`] = uid;
    });
    Object.entries(pivotData || {}).forEach(([uname, days]) => {
      if (map[uname]) return;
      const firstDay = Object.values(days)[0];
      if (firstDay?.user_id) map[uname] = String(firstDay.user_id);
    });
    return map;
  }, [usersForFilterLookup, pivotData]);

  const isUserAbsent = (username, dayNumber) => {
    const uid = userIdByUsername[username];
    if (!uid) return null;
    return absencesByUserId[uid]?.get(dayNumber) || null;
  };

  // Tabella principale
  useEffect(() => {
    if (!filtersReady) {
      return;
    }
    if (!managedUsersInitialized) {
      return;
    }
    if (!managedUsersLoaded || managedUsers.length === 0) {
      return;
    }

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const selectedUserIds = [...appliedUserIdsForData].sort().join(',');
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0)
      ? filterActivityIds.sort().join(',')
      : 'all';

    const fetchKey = `${yearMonth}-${selectedUserIds}-${activityIdsKey}`;

    if (appliedUserIdsForData.length === 0) {
      lastPivotFetchKey.current = fetchKey;
      setPivotData({});
      setPivotLoading(false);
      setPivotError(null);
      return;
    }

    // Evita chiamate duplicate per la stessa combinazione di parametri
    if (isFetchingPivotData.current) {
      return;
    }

    // Se la chiave è la stessa, evita la chiamata (dati già caricati)
    if (lastPivotFetchKey.current === fetchKey) {
      return;
    }

    // Reset del flag se la chiave è diversa (nuovi parametri)
    if (lastPivotFetchKey.current && lastPivotFetchKey.current !== fetchKey) {
      isFetchingPivotData.current = false;
    }

    isFetchingPivotData.current = true;
    lastPivotFetchKey.current = fetchKey;
    setPivotLoading(true);
    setPivotError(null);

    const fetchPivotData = async () => {
      try {
        const userIdsArray = appliedUserIdsForData;

        const allWorklogs = await fetchMonthlyWorklogSummaryForUsers(
          userIdsArray,
          yearMonth,
          filterActivityIds.length > 0 ? filterActivityIds : []
        );

        // Raggruppa per utente
        const byUser = {};
        allWorklogs.forEach(item => {
          const userName = item.username || usersForFilterLookup.find(u => u.id === item.user_id)?.name || 'Unknown User';
          if (!byUser[userName]) byUser[userName] = {};
          const day = parseLocalDate(item.work_date).getDate();
          byUser[userName][day] = accumulateMonthlySummaryCell(byUser[userName][day], item);
        });

        const selectedUserNames = sortUsersByDisplayName(resolveAppliedUserNames());
        selectedUserNames.forEach(name => {
          if (!byUser[name]) {
            byUser[name] = {};
          }
        });

        setPivotData(byUser);
        isFetchingPivotData.current = false;
      } catch (err) {
        console.error('Error in fetchPivotData:', err);
        setPivotError(`Error loading data: ${err.message}`);
        isFetchingPivotData.current = false;
      } finally {
        setPivotLoading(false);
      }
    };

    fetchPivotData();
  }, [filtersReady, managedUsersInitialized, filterMonth, filterYear, managedUsers, usersForFilterLookup, filterUsers, filterActivityIds, managedUsersLoaded, appliedUserIdsForData, userFilterMode, resolveAppliedUserNames]);

  // Reset della selezione utente quando cambiano i filtri
  useEffect(() => {
    setSelectedUserForDetails(null);
  }, [filterUsers, filterMonth, filterYear, userFilterMode]);

  // Tabella dettagliata: fetch pivot per utente selezionato (mostra progetti per quell'utente quando si clicca sul nome)
  useEffect(() => {
    if (!selectedUserForDetails) {
      setTeamDetailsLoading(false);
      return;
    }

    setTeamDetailsLoading(true);

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const selectedUserId = usersForFilterLookup.find(u => (u.name || u.username) === selectedUserForDetails)?.id;

    if (!selectedUserId) {
      setTeamDetailsLoading(false);
      return;
    }

    const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
      ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
      : '';
    fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${encodeURIComponent(selectedUserId)}&year_month=${yearMonth}${activityIdsParam}&include_forecast=true`)
      .then(res => res.json())
      .then(data => {
        const byProject = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            const projectName = item.project_name || 'Unknown Project';
            if (!byProject[projectName]) byProject[projectName] = {};
            const day = parseLocalDate(item.work_date).getDate();
            byProject[projectName][day] = accumulateMonthlySummaryCell(byProject[projectName][day], item);
          });
        }
        setDetailsPivotData(byProject);
        setTeamDetailsLoading(false);
      })
      .catch(err => {
        console.error('Error loading details:', err);
        setTeamDetailsLoading(false);
      });
  }, [selectedUserForDetails, filterMonth, filterYear, usersForFilterLookup, filterActivityIds]);

  // Per People Manager View, i dettagli mostrano progetti per l'utente selezionato
  // Non serve aggregazione per team, i dettagli sono già per progetto
  useEffect(() => {
    if (!selectedUserForDetails) {
      setTeamPivotData({});
      return;
    }
    // I dettagli sono già per progetto, quindi usiamo direttamente detailsPivotData
    setTeamPivotData(detailsPivotData || {});
  }, [detailsPivotData, selectedUserForDetails]);

  // Funzione per renderizzare la tabella pivot dettagliata per progetti
  function renderProjectPivotTable({ projectPivotData, filterYear, filterMonth, selectedUsername, isUserAbsentFn }) {
    const allProjects = projectPivotData ? Object.keys(projectPivotData) : [];
    if (allProjects.length === 0) return null;

    const daysInMonthLocal = new Date(filterYear, filterMonth, 0).getDate();
    const weekDays = Array.from({ length: daysInMonthLocal }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
    const weekendIndexes = weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay();
      return (day === 0 || day === 6);
    });

    const projectTotals = allProjects.map(p => {
      if (projectPivotData && projectPivotData[p]) {
        return Math.round((Object.entries(projectPivotData[p]).reduce((sum, [day, obj]) => (
          sum + pivotMinutesForDay(obj, filterYear, filterMonth, Number(day))
        ), 0) / 60) * 100) / 100;
      }
      return 0;
    });

    const dayTotals = Array.from({ length: daysInMonthLocal }, (_, i) => {
      let total = 0;
      allProjects.forEach(p => {
        total += pivotMinutesForDay(projectPivotData?.[p]?.[i + 1], filterYear, filterMonth, i + 1);
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(projectTotals.reduce((a, b) => a + b, 0) * 100) / 100;

    return (
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        bgcolor: 'white',
        p: 0,
        maxWidth: '100vw',
        overflowX: 'auto',
        boxShadow: 'none',
        border: '1px solid #f3f3f3',
        mt: 4
      }}>
        <Table size="small" sx={{
          minWidth: tableDimensions.minWidth,
          tableLayout: 'fixed',
          fontSize: '0.85rem',
          width: '100%',
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonthLocal }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                border: 'none',
                borderRight: '0px solid #e0e0e0',
                textAlign: 'center',
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                Project
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                p: 0.5,
                border: '0px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 3,
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonthLocal }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allProjects.map((p, idx) => (
              <TableRow
                key={p}
                hover
                sx={{ '&:hover': { bgcolor: '#e6f4ea' } }}
              >
                <TableCell
                  className="pivot-user-col"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    minWidth: 120,
                    maxWidth: 160,
                    width: 140,
                    p: 0.5,
                    border: '1px solid #f3f3f3',
                    borderRight: '2px solid #5ab031',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    bgcolor: 'white'
                  }}
                >
                  {p}
                </TableCell>
                {Array.from({ length: daysInMonthLocal }, (_, i) => {
                  const obj = projectPivotData?.[p]?.[i + 1];
                  const dayNum = i + 1;
                  const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const future = isFutureDay(dateStr);
                  const minutes = future ? (Number(obj?.forecast_effort) || 0) : (Number(obj?.actual_effort ?? obj?.total_effort) || 0);
                  const displayValue = minutes ? Math.round((minutes / 60) * 100) / 100 : '';
                  const absEntry = selectedUsername && isUserAbsentFn ? isUserAbsentFn(selectedUsername, dayNum) : null;
                  const isWeekend = weekendIndexes[i];
                  return (
                    <MuiTooltip key={i + 1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${displayValue ? ` · ${displayValue}h` : ''}${future ? ' (forecast)' : ''}` : (future ? 'Forecast' : '')} arrow>
                    <TableCell sx={{
                      textAlign: 'center',
                      bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : (future && obj?.forecast_effort ? FORECAST_CONFIG.forecastCellBg : 'white'),
                      color: future && displayValue ? FORECAST_CONFIG.forecastCellColor : 'inherit',
                      fontSize: '0.7rem',
                      p: 0.5,
                      border: '1px solid #f3f3f3',
                      fontWeight: future && displayValue ? 600 : 'normal',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontVariantNumeric: 'tabular-nums',
                      '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                    }}>
                      {displayValue ? displayValue : absEntry ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : ''}
                    </TableCell>
                    </MuiTooltip>
                  );
                })}
                <TableCell sx={{
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  borderLeft: '2px solid #5ab031',
                  minWidth: 60,
                  maxWidth: 80,
                  width: 60,
                  bgcolor: 'white !important',
                  color: 'black !important',
                  fontWeight: 'bold',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>{projectTotals[idx]}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell
                className="pivot-user-col"
                sx={{
                  bgcolor: 'white',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: 120,
                  maxWidth: 160,
                  width: 140,
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  borderRight: '2px solid #5ab031',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                  borderTop: '2px solid #5ab031',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>Tot</TableCell>
              {dayTotals.map((tot, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{tot}</TableCell>
              ))}
              <TableCell sx={{
                bgcolor: 'white !important',
                color: 'black !important',
                textAlign: 'center',
                fontSize: '0.7rem',
                p: 0.5,
                border: '1px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 2,
                borderLeft: '2px solid #5ab031',
                borderTop: '2px solid #5ab031',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                fontWeight: 'bold',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Ordina gli utenti alfabeticamente per una visualizzazione consistente
  const sortedPivotRows = useMemo(() => (
    Object.entries(pivotData).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    )
  ), [pivotData]);

  const filteredPivotData = useMemo(() => (
    Object.fromEntries(sortedPivotRows)
  ), [sortedPivotRows]);

  // Calcola totali per riga e per giorno (basati sui dati filtrati, convertiti da minuti a ore)
  const rowTotals = useMemo(() => {
    const totals = {};
    Object.entries(filteredPivotData).forEach(([rowKey, days]) => {
      totals[rowKey] = Math.round((Object.entries(days).reduce((sum, [day, obj]) => (
        sum + pivotMinutesForDay(obj, filterYear, filterMonth, Number(day))
      ), 0) / 60) * 100) / 100;
    });
    return totals;
  }, [filteredPivotData, filterYear, filterMonth]);
  const dayTotals = useMemo(() => {
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = Math.round((Object.values(filteredPivotData).reduce((sum, days) => (
        sum + pivotMinutesForDay(days[d], filterYear, filterMonth, d)
      ), 0) / 60) * 100) / 100;
    }
    return totals;
  }, [filteredPivotData, daysInMonth, filterYear, filterMonth]);
  const grandTotal = useMemo(() => Math.round(Object.values(rowTotals).reduce((sum, v) => sum + v, 0) * 100) / 100, [rowTotals]);

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>Monthly People Manager View</Typography>
        </Box>
        {/* Filtri progetto e intervallo date */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
            multiple
            disabled={!managedUsersLoaded}
            disableCloseOnSelect={true}
            loading={hierarchyUsersLoading}
            onOpen={() => { ensureHierarchyLoaded().catch(() => {}); }}
            options={[
              { id: 'all-direct', name: 'All direct reports', isSpecial: true },
              { id: 'all-hierarchy', name: 'All reports', isSpecial: true },
              { id: 'all-indirect', name: 'Indirect reports', isSpecial: true },
              ...searchAutocompleteOptions,
            ]}
            getOptionLabel={(option) => option.name || option.username || option.email}
            value={
              pendingUserFilterMode === 'custom'
                ? usersForFilterLookup.filter(u => pendingFilterUsers.includes(u.name || u.username))
                : []
            }
            onChange={(event, newValue) => {
              if (newValue.some(v => v.id === 'all-direct')) {
                setPendingUserFilterMode('direct');
                setPendingFilterUsers([]);
                return;
              }
              if (newValue.some(v => v.id === 'all-hierarchy')) {
                selectHierarchyModePending('all');
                return;
              }
              if (newValue.some(v => v.id === 'all-indirect')) {
                selectHierarchyModePending('indirect');
                return;
              }
              setPendingUserFilterMode('custom');
              setPendingFilterUsers(newValue.map(u => u.name || u.username));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Managed Users"
                placeholder="Search users..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                const checked = option.id === 'all-direct'
                  ? pendingUserFilterMode === 'direct'
                  : option.id === 'all-hierarchy'
                    ? pendingUserFilterMode === 'all'
                    : pendingUserFilterMode === 'indirect';
                const showSpinner = hierarchyUsersLoading
                  && (option.id === 'all-hierarchy' || option.id === 'all-indirect');
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={checked}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                    {showSpinner && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                );
              }

              return (
                <Box component="li" {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {option.name || option.username}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {option.email}
                      </Typography>
                    )}
                    {option.isNested && option.peopleManagerName && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        People Manager: {option.peopleManagerName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={() => {
              if (pendingUserFilterMode === 'all') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    All reports ({allHierarchyUserCount})
                  </Typography>
                );
              }
              if (pendingUserFilterMode === 'indirect') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    Indirect reports ({indirectUsers.length})
                  </Typography>
                );
              }
              if (pendingUserFilterMode === 'direct') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    All direct reports ({managedUsers.length})
                  </Typography>
                );
              }
              return (
                <Typography variant="body2">
                  {pendingFilterUsers.length} selected
                </Typography>
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No users found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              // Always show special options at the top
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);

              // Filter regular options based on input
              const query = inputValue.toLowerCase();
              const filteredRegular = sortUsersByDisplayName(regularOptions.filter((option) =>
                (option.name || option.username || '').toLowerCase().includes(query) ||
                (option.email && option.email.toLowerCase().includes(query)) ||
                (option.peopleManagerName && option.peopleManagerName.toLowerCase().includes(query))
              ));

              return [...specialOptions, ...filteredRegular];
            }}
          />
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="filter-month-label">Month</InputLabel>
              <Select
                labelId="filter-month-label"
                value={pendingFilterMonth}
                label="Month"
                onChange={e => setPendingFilterMonth(Number(e.target.value))}
              >
                {monthNames.map((name, idx) => (
                  <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="Next month">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Filtro Activity */}
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...activities.map(ac => ({ id: ac.id, name: ac.name }))
            ]}
            getOptionLabel={(option) => option.name}
            value={activities
              .filter(ac => pendingFilterActivityIds.includes(ac.id))
              .map(ac => ({ id: ac.id, name: ac.name }))}
            onChange={(event, newValue) => {
              const hasAll = newValue.some(v => v.id === 'all');
              if (hasAll) {
                setPendingFilterActivityIds([]);
              } else {
                setPendingFilterActivityIds(newValue.map(v => v.id));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Activities"
                placeholder="Search activities..."
                size="small"
                sx={{ minWidth: 260 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? pendingFilterActivityIds.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box component="li" {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              );
            }}
            renderTags={(value) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All activities
                </Typography>
              ) : value.length === 1 && value[0].id === 'all' ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                  All
                </Typography>
              ) : (
                <Typography variant="body2">{value.length} selected</Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No activities found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              const filteredRegular = regularOptions.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              );
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="filter-year-label">Year</InputLabel>
            <Select
              labelId="filter-year-label"
              value={pendingFilterYear}
              label="Year"
              onChange={e => setPendingFilterYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={applyFilters} disabled={!filtersDirty}>
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={resetFilters}>Reset</Button>
        </Box>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
          <Chip label={activeFilterSummary} size="small" variant="outlined" />
          {filtersDirty && (
            <Chip label="Unapplied changes" size="small" color="warning" variant="outlined" />
          )}
        </Box>
        {hierarchyLoadError ? (
          <Alert severity="warning" sx={{ mb: 1 }}>{hierarchyLoadError}</Alert>
        ) : null}
        {managedUsersLoaded && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={exportAbsencesLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={exportAbsencesLoading || !userId}
              onClick={handleExportHierarchyAbsences}
            >
              {exportAbsencesLoading ? 'Exporting...' : 'Export absences'}
            </Button>
          </Box>
        )}
        {!managedUsersLoaded ? (
          <CircularProgress />
        ) : pivotError ? (
          <Alert severity="error">{pivotError}</Alert>
        ) : (Object.keys(filteredPivotData).length === 0 && !pivotLoading) ? (
          <Alert severity="info">{
            userFilterMode === 'indirect' && appliedUserIdsForData.length === 0
              ? 'No indirect reports found in your hierarchy.'
              : userFilterMode === 'custom' && filterUsers.length > 0
                ? 'No worklogs found for the selected user(s) in this period.'
                : 'For this date, no worklogs are present.'
          }</Alert>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {pivotLoading && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.6)',
              }}>
                <CircularProgress size={32} />
              </Box>
            )}
          <>
            <TableContainer component={Paper} sx={{
              borderRadius: 2,
              bgcolor: 'white',
              p: 0,
              maxWidth: '100vw',
              overflowX: 'auto',
              boxShadow: 'none',
              border: '1px solid #f3f3f3',
              opacity: pivotLoading ? 0.55 : 1,
              transition: 'opacity 0.2s',
            }}>
              <Table size="small" sx={{
                minWidth: tableDimensions.minWidth,
                tableLayout: 'fixed',
                fontSize: '0.85rem',
                width: '100%',
                bgcolor: 'white'
              }}>
                <colgroup>
                  <col style={{ width: tableDimensions.firstColWidth }} />
                  {Array.from({ length: daysInMonth }).map((_, i) => (
                    <col key={i} style={{ width: tableDimensions.dayColWidth }} />
                  ))}
                  <col style={{ width: tableDimensions.totalColWidth }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className="pivot-user-col"
                      rowSpan={2}
                      sx={{
                        bgcolor: '#5ab031',
                        border: 'none',
                        p: 0,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        color: 'white',
                        fontWeight: 'normal',
                        borderRight: '0px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      User
                    </TableCell>
                    {weekDays.map((wd, i) => {
                      return (
                        <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
                      );
                    })}
                    <TableCell sx={{
                      bgcolor: '#5ab031',
                      color: 'white',
                      fontWeight: 'normal',
                      fontSize: '0.85rem',
                      p: 0.5,
                      border: '0px solid #f3f3f3',
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 3,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }} rowSpan={2}>Tot</TableCell>
                  </TableRow>
                  <TableRow>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      return (
                        <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
                      );
                    })}
                    {/* Nessuna TableCell qui per Tot, perché la cella sopra ha rowSpan=2 */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPivotRows.map(([rowKey, days]) => (
                    <TableRow
                      key={rowKey}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#e6f4ea' }
                      }}
                    >
                      <TableCell
                        className="pivot-user-col"
                        sx={{
                          bgcolor: 'white',
                          color: '#5ab031',
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          minWidth: 120,
                          maxWidth: 160,
                          width: 140,
                          p: 0.5,
                          border: '1px solid #f3f3f3',
                          borderRight: '2px solid #5ab031',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'left',
                          '@media (max-width: 900px)': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <MuiTooltip title="View project details" arrow>
                            <span
                              style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontWeight: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = '#4a9b2a';
                                e.target.style.fontWeight = 'bold';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = '#5ab031';
                                e.target.style.fontWeight = 'normal';
                              }}
                              onClick={() => {
                                if (selectedUserForDetails === rowKey) {
                                  setSelectedUserForDetails(null);
                                } else {
                                  setSelectedUserForDetails(rowKey);
                                }
                              }}
                            >
                              {formatUserName(rowKey)}
                            </span>
                          </MuiTooltip>
                          {(() => {
                            const userObj = usersForFilterLookup.find(u => (u.name || u.username) === rowKey);
                            return userObj?.isPeopleManager ? (
                              <MuiTooltip title="View hierarchy" arrow>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(90, 176, 49, 0.1)',
                                    borderRadius: '4px',
                                    padding: '2px 4px',
                                    marginLeft: '4px',
                                    flexShrink: 0,
                                    '&:hover': {
                                      bgcolor: 'rgba(90, 176, 49, 0.2)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/monthly-people-mng-view/user/${userObj.id}`, {
                                      state: {
                                        filterYear,
                                        filterMonth,
                                        filterActivityIds,
                                        from: '/monthly-people-mng-view',
                                        selectedUser: {
                                          id: userObj.id,
                                          name: userObj.name || userObj.username,
                                          username: userObj.username,
                                          email: userObj.email
                                        }
                                      }
                                    });
                                  }}
                                >
                                  <SupervisorAccountIcon 
                                    sx={{ 
                                      fontSize: '1.1rem', 
                                      color: '#5ab031'
                                    }}
                                  />
                                </Box>
                              </MuiTooltip>
                            ) : null;
                          })()}
                        </Box>
                      </TableCell>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const obj = days[i + 1];
                        const dayNum = i + 1;
                        const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                        const future = isFutureDay(dateStr);
                        const minutes = pivotMinutesForDay(obj, filterYear, filterMonth, dayNum);
                        const value = minutes ? Math.round((minutes / 60) * 100) / 100 : '';
                        const isWeekend = weekendIndexes[i];
                        const absEntry = isUserAbsent(rowKey, dayNum);
                        return (
                          <MuiTooltip key={i + 1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${value ? ` · ${value}h` : ''}${future ? ' (forecast)' : ''}` : (future ? 'Forecast' : '')} arrow>
                          <TableCell sx={{
                            textAlign: 'center',
                            bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : (future && obj?.forecast_effort ? FORECAST_CONFIG.forecastCellBg : 'white'),
                            color: future && value ? FORECAST_CONFIG.forecastCellColor : '#374151',
                            fontSize: '0.7rem',
                            p: 0.5,
                            border: '1px solid #f3f3f3',
                            fontWeight: future && value ? 600 : 'normal',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontVariantNumeric: 'tabular-nums',
                            '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                          }}>
                            {obj ? (
                              <MuiTooltip
                                title={
                                  <span>
                                    {future && obj.forecast_effort ? <div><b>Forecast:</b> {Math.round((Number(obj.forecast_effort) / 60) * 100) / 100} h</div> : null}
                                    {!future && obj.total_effort ? <div><b>Effort:</b> {Math.round((Number(obj.actual_effort ?? obj.total_effort) / 60) * 100) / 100} h</div> : null}
                                    {obj.ticket_id && <div><b>Ticket:</b> {obj.ticket_id}</div>}
                                    {obj.description && <div><b>Desc:</b> {obj.description}</div>}
                                    {obj.activity_name && <div><b>Activity:</b> {obj.activity_name}</div>}
                                  </span>
                                }
                                arrow
                              >
                                <span>{value}</span>
                              </MuiTooltip>
                            ) : absEntry ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : value}
                          </TableCell>
                          </MuiTooltip>
                        );
                      })}
                      <TableCell sx={{
                        bgcolor: 'white !important',
                        color: 'black !important',
                        textAlign: 'center',
                        fontSize: '0.7rem',
                        p: 0.5,
                        border: '1px solid #f3f3f3',
                        borderLeft: '2px solid #5ab031',
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontWeight: 'bold',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{rowTotals[rowKey]}</TableCell>
                    </TableRow>
                  ))}
                  {/* Riga totale per giorno (ripristinata) */}
                  <TableRow sx={{ bgcolor: '#248b7e' }}>
                    <TableCell
                      sx={{
                        bgcolor: 'white',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        p: 0.5,
                        border: '1px solid #f3f3f3',
                        borderRight: '2px solid #5ab031',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'right',
                        borderTop: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}
                      className="pivot-user-col"
                    >
                      Tot
                    </TableCell>
                    {Array.from({ length: daysInMonth }, (_, i) => (
                      <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{dayTotals[i + 1]}</TableCell>
                    ))}
                    <TableCell sx={{
                      bgcolor: 'white !important',
                      color: 'black !important',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      p: 0.5,
                      border: '1px solid #f3f3f3',
                      borderLeft: '2px solid #5ab031',
                      borderTop: '2px solid #5ab031',
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}>{grandTotal}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
          </Box>
        )}

        {/* Tabella dettagliata inline per utenti non-People Manager */}
        {selectedUserForDetails && (
          <Box sx={{ mt: 3 }}>
            {teamDetailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
                  Loading project details...
                </Typography>
              </Box>
            ) : teamPivotData && Object.keys(teamPivotData).length > 0 ? (
              renderProjectPivotTable({
                projectPivotData: teamPivotData,
                filterYear,
                filterMonth,
                selectedUsername: selectedUserForDetails,
                isUserAbsentFn: isUserAbsent,
              })
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No project data available for this user in the selected period.
              </Alert>
            )}
          </Box>
        )}

        {/* Note about effort in hours */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 1,
          pr: 1
        }}>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}
          >
            * effort in hours
          </Typography>
        </Box>
      </Box>

      {/* Informational note */}
      <Box sx={{
        mt: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          💡 <strong>Tip:</strong> Click on a user name to view detailed hours breakdown by project for that user for the selected month.
        </Typography>
      </Box>
    </Box>
  );
} 